import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Card, Badge, Button, InputGroup, ButtonGroup, ToggleButton } from 'react-bootstrap';
import { useCampaign } from '../CampaignContext';
import { countPlaceholders, SYSTEM_VARIABLES } from '../campaignUtils';

const Step3Template = () => {
    const { state, dispatch } = useCampaign();
    const { template } = state;
    
    // Mock templates (should be fetched from API in real implementation)
    const [templates] = useState([
        { id: 1, template_name: 'Mid-term Reminder', body: 'Hello {{1}}, this is a reminder for your mid-term exam at {{2}} branch. Please be there by {{3}}.', header: { type: 'IMAGE', url: 'https://via.placeholder.com/600x300' }, footer: 'Rizee College' },
        { id: 2, template_name: 'Fee Due', body: 'Dear Parents, the fee for {{1}} is due. Total amount: {{2}}. Please pay by {{3}}.', header: { type: 'TEXT', text: 'URGENT: FEE REMINDER' }, footer: 'Accounts Department' },
        { id: 3, template_name: 'Welcome Message', body: 'Welcome {{1}} to Rizee College! We are happy to have you in our {{2}} branch.', header: { type: 'NONE' }, footer: '' }
    ]);

    const handleTemplateSelection = (e) => {
        const selected = templates.find(t => String(t.id) === String(e.target.value));
        if (selected) {
            const placeholdersCount = countPlaceholders(selected.body);
            const variables = Array.from({ length: placeholdersCount }, (_, i) => ({
                index: i + 1,
                mode: 'dynamic', // default
                value: '',
                dynamic_key: i === 0 ? 'student_name' : '' // auto-suggest first var as name
            }));

            dispatch({ 
                type: 'UPDATE_TEMPLATE', 
                payload: { 
                    id: selected.id, 
                    name: selected.template_name, 
                    body: selected.body,
                    header: selected.header,
                    footer: selected.footer,
                    variables 
                } 
            });
        }
    };

    const handleVariableChange = (index, field, value) => {
        const newVariables = [...template.variables];
        const varIdx = newVariables.findIndex(v => v.index === index);
        if (varIdx !== -1) {
            newVariables[varIdx][field] = value;
            dispatch({ type: 'UPDATE_TEMPLATE', payload: { variables: newVariables } });
        }
    };

    // Helper to render the live preview text
    const getPreviewText = () => {
        let text = template.body || "Select a template to see preview...";
        template.variables.forEach(v => {
            const placeholder = `{{${v.index}}}`;
            const displayValue = v.mode === 'static' ? (v.value || placeholder) : `[${v.dynamic_key || 'Select Variable'}]`;
            text = text.replace(placeholder, `*${displayValue}*`);
        });
        return text;
    };

    return (
        <div className="py-2">
            <h5 className="font-weight-bold mb-3">Message & Template Engine</h5>
            
            <Row>
                {/* Left Panel: Configuration */}
                <Col md={7}>
                    <Form.Group className="mb-4">
                        <Form.Label className="small font-weight-bold">SELECT APPROVED TEMPLATE</Form.Label>
                        <Form.Control as="select" className="wcm-input-sm font-weight-bold" value={template.id || ''} onChange={handleTemplateSelection}>
                            <option value="">-- Select Template --</option>
                            {templates.map(t => <option key={t.id} value={t.id}>{t.template_name}</option>)}
                        </Form.Control>
                    </Form.Group>

                    {template.id && (
                        <div className="template-variables-mapping mt-4">
                            <h6 className="font-weight-bold mb-3 text-muted" style={{ fontSize: '11px', letterSpacing: '1px' }}>PLACEHOLDER MAPPING</h6>
                            {template.variables.map((v, idx) => (
                                <Card key={v.index} className="mb-3 border rounded shadow-sm bg-white p-3">
                                    <div className="d-flex justify-content-between align-items-center mb-2">
                                        <Badge variant="primary" className="px-2 py-1" style={{ fontSize: '10px' }}>PLACEHOLDER {v.index}: {"{{" + v.index + "}}"}</Badge>
                                        <ButtonGroup className="shadow-none">
                                            <Button 
                                                variant={v.mode === 'dynamic' ? 'primary' : 'outline-primary'} 
                                                size="sm" 
                                                className="px-3"
                                                onClick={() => handleVariableChange(v.index, 'mode', 'dynamic')}
                                            >
                                                Dynamic
                                            </Button>
                                            <Button 
                                                variant={v.mode === 'static' ? 'primary' : 'outline-primary'} 
                                                size="sm" 
                                                className="px-3"
                                                onClick={() => handleVariableChange(v.index, 'mode', 'static')}
                                            >
                                                Static
                                            </Button>
                                        </ButtonGroup>
                                    </div>
                                    {v.mode === 'dynamic' ? (
                                        <Form.Control 
                                            as="select" 
                                            size="sm" 
                                            className="font-weight-bold"
                                            value={v.dynamic_key}
                                            onChange={(e) => handleVariableChange(v.index, 'dynamic_key', e.target.value)}
                                        >
                                            <option value="">-- Choose System Variable --</option>
                                            {SYSTEM_VARIABLES.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </Form.Control>
                                    ) : (
                                        <Form.Control 
                                            type="text" 
                                            size="sm" 
                                            placeholder="Enter static text value..."
                                            value={v.value}
                                            onChange={(e) => handleVariableChange(v.index, 'value', e.target.value)}
                                        />
                                    )}
                                </Card>
                            ))}
                        </div>
                    )}
                </Col>

                {/* Right Panel: WhatsApp Preview */}
                <Col md={5}>
                    <div className="sticky-top" style={{ top: '20px' }}>
                        <h6 className="font-weight-bold mb-3 text-muted text-center" style={{ fontSize: '11px' }}>CAMPAIGN PREVIEW</h6>
                        <div className="wcm-wp-container p-4">
                            {/* The WhatsApp Bubble */}
                            <Card className="wcm-wp-bubble border-0">
                                <Card.Body className="p-2 position-relative">
                                    {/* Header */}
                                    {template.header?.type === 'IMAGE' && template.header.url && (
                                        <img src={template.header.url} className="w-100 rounded mb-2" alt="Header" />
                                    )}
                                    {template.header?.type === 'TEXT' && template.header.text && (
                                        <div className="font-weight-bold text-dark mb-2 small" style={{ opacity: 0.8 }}>{template.header.text}</div>
                                    )}
                                    
                                    {/* Body */}
                                    <div className="whatsapp-body text-dark small" style={{ fontSize: '12.5px', whiteSpace: 'pre-wrap' }}>
                                        {getPreviewText()}
                                    </div>
                                    
                                    {/* Footer */}
                                    {template.footer && (
                                        <div className="text-muted mt-2 border-top pt-1" style={{ fontSize: '10px' }}>
                                            {template.footer}
                                        </div>
                                    )}
                                    
                                    {/* Timestamp */}
                                    <div className="text-right mt-1" style={{ fontSize: '9px', color: '#666' }}>
                                        {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })} <i className="fas fa-check-double text-primary ml-1" />
                                    </div>
                                </Card.Body>
                            </Card>
                            
                            {/* Buttons */}
                            <div className="mt-2 w-100" style={{ maxWidth: '90%', marginLeft: 'auto' }}>
                                <Card className="border-0 py-2 px-3 text-center mb-1 text-primary small shadow-sm" style={{ borderRadius: '8px' }}>
                                    <i className="fas fa-external-link-alt mr-2" /> View Website
                                </Card>
                                <Card className="border-0 py-2 px-3 text-center text-primary small shadow-sm" style={{ borderRadius: '8px' }}>
                                    <i className="fas fa-reply mr-2" /> Opt-out
                                </Card>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
        </div>
    );
};

export default Step3Template;
